// Адмін: генератор Telegram-опитувань.
//
// Натиснув "🎲 Згенерувати" → отримав випадкове питання → дві опції:
//   - "📋 Скопіювати" — копіює готовий текст для ручного постингу
//   - "📤 Опублікувати" — бот сам публікує в канал (потрібен налаштований channel)
import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { Loader } from '../../components/Loader';

type QuizData = {
  questionId: number;
  questionText: string;
  questionTextFull: string;
  imageUrl: string | null;
  category: { id: number; nameUk: string };
  explanation: string | null;
  options: Array<{ text: string; textFull: string; isCorrect: boolean }>;
  correctIndex: number;
  copyText: string;
};

export function AdminQuiz() {
  const [data, setData] = useState<QuizData | null>(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [channelOverride, setChannelOverride] = useState('');
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function generate() {
    setLoading(true);
    setMessage(null);
    setCopied(false);
    try {
      const res = await api.admin.quizRandom();
      if ('error' in res) {
        setMessage({ kind: 'err', text: 'Питання не знайдено' });
      } else {
        setData(res);
      }
    } catch (e) {
      setMessage({
        kind: 'err',
        text: e instanceof ApiError ? e.message : 'Помилка генерації',
      });
    } finally {
      setLoading(false);
    }
  }

  function copyText() {
    if (!data) return;
    navigator.clipboard.writeText(data.copyText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function publish() {
    if (!data || publishing) return;
    if (
      !confirm(
        'Опублікувати в каналі прямо зараз?\n\nБот має бути додано адміном цього каналу. Якщо канал не налаштовано в /admin/settings — бот може попросити вказати його.',
      )
    )
      return;
    setPublishing(true);
    setMessage(null);
    try {
      const res = await api.admin.quizPublish(
        data.questionId,
        channelOverride.trim() || undefined,
      );
      if ('error' in res) {
        setMessage({
          kind: 'err',
          text: `${res.message}${'hint' in res && res.hint ? `\n\n${res.hint}` : ''}`,
        });
      } else {
        setMessage({
          kind: 'ok',
          text: `✅ Опубліковано в ${res.channelId}! Message ID: ${res.messageId}${
            res.warning ? `\n\n⚠️ ${res.warning}` : ''
          }`,
        });
        // Одразу генеруємо наступне питання щоб публікувати ще
        await generate();
      }
    } catch (e) {
      setMessage({
        kind: 'err',
        text: e instanceof ApiError ? e.message : 'Помилка публікації',
      });
    } finally {
      setPublishing(false);
    }
  }

  return (
    <div className="space-y-3 p-4">
      {/* Авто-постинг за розкладом */}
      <AutoPostCard />

      {/* Заголовок + кнопка генерації */}
      <div className="card space-y-3">
        <div>
          <h2 className="text-base font-semibold">🎲 Генератор опитувань для каналу</h2>
          <p className="mt-1 text-xs text-tg-hint">
            Згенерує випадкове питання з твоєї бази у форматі Telegram Quiz Poll.
            Бот публікує картинку + опитування одним кліком (потрібно додати бота адміном
            цього каналу).
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={generate}
            disabled={loading}
            className="btn-primary flex-1"
          >
            {loading ? '⏳ Шукаю...' : '🎲 Згенерувати випадкове питання'}
          </button>
        </div>

        <div>
          <label className="block text-xs text-tg-hint">Канал (опційно — переписати з налаштувань)</label>
          <input
            value={channelOverride}
            onChange={(e) => setChannelOverride(e.target.value)}
            placeholder="@мій_канал або -100123456789"
            className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
          />
          <p className="mt-1 text-[11px] text-tg-hint">
            Якщо порожньо — бот візьме канал з налаштування <code>quizChannelId</code> у /admin/settings.
          </p>
        </div>
      </div>

      {/* Сповіщення про результат */}
      {message && (
        <div
          className={`card whitespace-pre-line text-sm ${
            message.kind === 'ok'
              ? 'border border-tg-success text-tg-success'
              : 'border border-tg-destructive text-tg-destructive'
          }`}
        >
          {message.text}
        </div>
      )}

      {loading && !data && <Loader />}

      {/* Превʼю згенерованого питання */}
      {data && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className="rounded-full bg-tg-button/15 px-2.5 py-0.5 text-[11px] font-semibold text-tg-button">
              📚 {data.category.nameUk}
            </span>
            <span className="text-[11px] text-tg-hint">ID #{data.questionId}</span>
          </div>

          {data.imageUrl && (
            <img
              src={data.imageUrl}
              alt=""
              className="w-full rounded-lg bg-tg-bg object-contain"
              loading="lazy"
            />
          )}

          <p className="text-base font-medium leading-snug">{data.questionTextFull}</p>

          <div className="space-y-1.5">
            {data.options.map((opt, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 rounded-lg p-2.5 text-sm ${
                  opt.isCorrect
                    ? 'border border-tg-success bg-tg-success/10'
                    : 'bg-tg-bg/40'
                }`}
              >
                <span
                  className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${
                    opt.isCorrect ? 'bg-tg-success text-white' : 'bg-tg-secondary-bg'
                  }`}
                >
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="flex-1">{opt.textFull}</span>
                {opt.isCorrect && <span className="text-tg-success">✓</span>}
              </div>
            ))}
          </div>

          {data.explanation && (
            <div className="rounded-lg border border-tg-link/30 bg-tg-link/5 p-3">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-tg-link">
                💡 Пояснення (з'явиться після голосування)
              </div>
              <p className="text-sm leading-relaxed">{data.explanation}</p>
            </div>
          )}

          {/* Кнопки дій */}
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={copyText}
              className="flex-1 rounded-lg bg-tg-secondary-bg px-3 py-2.5 text-sm font-semibold"
            >
              {copied ? '✓ Скопійовано' : '📋 Скопіювати текст'}
            </button>
            <button
              onClick={publish}
              disabled={publishing}
              className="flex-1 rounded-lg bg-tg-success px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {publishing ? '⏳ Публікую...' : '📤 Опублікувати в каналі'}
            </button>
          </div>

          <p className="text-[11px] text-tg-hint">
            «Опублікувати» — бот шле картинку (якщо є) + опитування quiz з правильною
            відповіддю та поясненням. Юзери в каналі голосують, бачать що правильно і чому.
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Авто-постинг: вмикається + список часів (Kyiv) → шедулер у фоні публікує сам.
// Бекенд тримає налаштування в AppSetting; tick раз на хвилину перевіряє слот.
// ============================================================================
type AutoConfig = {
  enabled: boolean;
  times: string[];
  categoryId: number | null;
  channelId: string | null;
  defaultChannelId: string | null;
  lastPublishedAt: string | null;
  lastError: string | null;
};

const DEFAULT_PRESETS: { label: string; times: string[] }[] = [
  { label: '2 на день', times: ['10:00', '19:00'] },
  { label: '4 на день', times: ['09:00', '13:00', '17:00', '21:00'] },
  { label: '6 на день', times: ['08:00', '11:00', '14:00', '17:00', '20:00', '22:00'] },
  { label: 'Кожні 3 год (8–23)', times: ['08:00', '11:00', '14:00', '17:00', '20:00', '23:00'] },
];

function AutoPostCard() {
  const [cfg, setCfg] = useState<AutoConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [timesRaw, setTimesRaw] = useState('');
  const [channel, setChannel] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function reload() {
    setLoading(true);
    try {
      const res = await api.admin.quizAutoGet();
      setCfg(res);
      setEnabled(res.enabled);
      setTimesRaw(res.times.join(', '));
      setChannel(res.channelId ?? '');
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof ApiError ? e.message : 'Помилка завантаження' });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void reload();
  }, []);

  async function save() {
    const times = timesRaw
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setSaving(true);
    setMsg(null);
    try {
      const res = await api.admin.quizAutoSave({
        enabled,
        times,
        categoryId: null,
        channelId: channel.trim() || null,
      });
      if ('error' in res) {
        setMsg({ kind: 'err', text: res.message });
      } else {
        setMsg({ kind: 'ok', text: '✅ Збережено' });
        await reload();
      }
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof ApiError ? e.message : 'Помилка збереження' });
    } finally {
      setSaving(false);
    }
  }

  function applyPreset(times: string[]) {
    setTimesRaw(times.join(', '));
  }

  if (loading) {
    return (
      <div className="card">
        <Loader />
      </div>
    );
  }

  const fallbackChannel = cfg?.defaultChannelId;
  const lastAt = cfg?.lastPublishedAt
    ? new Date(cfg.lastPublishedAt).toLocaleString('uk-UA', {
        timeZone: 'Europe/Kiev',
        hour12: false,
      })
    : null;

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">⏰ Авто-постинг за розкладом</h2>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            enabled ? 'bg-tg-success/15 text-tg-success' : 'bg-tg-hint/15 text-tg-hint'
          }`}
        >
          {enabled ? 'УВІМК' : 'ВИМК'}
        </span>
      </div>

      <p className="text-xs text-tg-hint">
        Бот сам публікує випадкові питання у канал у вказані години (Kyiv).
        Перевіряє раз на хвилину — від реального часу можливо ±2 хв.
      </p>

      {/* Toggle */}
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 accent-tg-button"
        />
        <span className="text-sm">Увімкнути авто-постинг</span>
      </label>

      {/* Preset buttons */}
      <div>
        <div className="mb-1 text-[11px] text-tg-hint">Швидкі шаблони:</div>
        <div className="flex flex-wrap gap-1.5">
          {DEFAULT_PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => applyPreset(p.times)}
              type="button"
              className="rounded-lg bg-tg-secondary-bg px-2.5 py-1 text-xs"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Times input */}
      <div>
        <label className="block text-xs text-tg-hint">
          Час публікацій (HH:MM, через кому, час за Києвом)
        </label>
        <input
          value={timesRaw}
          onChange={(e) => setTimesRaw(e.target.value)}
          placeholder="09:00, 13:00, 17:00, 21:00"
          className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
        />
      </div>

      {/* Channel override */}
      <div>
        <label className="block text-xs text-tg-hint">
          Канал {fallbackChannel ? `(порожньо = ${fallbackChannel})` : '(якщо нема quizChannelId у settings)'}
        </label>
        <input
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          placeholder="@мій_канал або -100123456789"
          className="mt-1 w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
        />
      </div>

      {/* Save */}
      <button
        onClick={save}
        disabled={saving}
        className="btn-primary w-full disabled:opacity-50"
      >
        {saving ? '⏳ Зберігаю...' : '💾 Зберегти налаштування'}
      </button>

      {msg && (
        <div
          className={`rounded-lg p-2 text-xs ${
            msg.kind === 'ok'
              ? 'border border-tg-success text-tg-success'
              : 'border border-tg-destructive text-tg-destructive'
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* Status */}
      <div className="space-y-1 border-t border-tg-secondary-bg pt-2 text-[11px] text-tg-hint">
        {lastAt && (
          <div>
            🕐 Остання авто-публікація: <span className="text-tg-text">{lastAt}</span>
          </div>
        )}
        {cfg?.lastError && (
          <div className="text-tg-destructive">⚠️ Остання помилка: {cfg.lastError}</div>
        )}
        {enabled && cfg?.times && cfg.times.length > 0 && (
          <div>
            📅 Активний розклад: <span className="text-tg-text">{cfg.times.join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );
}
